/**
 * Jurisdiction Selector Component
 * Shows supported counties prominently to help users understand coverage area
 * before they begin creating a plan
 */

import { useCounties } from '../../hooks/useCounties';

export function JurisdictionSelector() {
  const { counties, loading, error } = useCounties();

  // Show loading skeleton
  if (loading) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-6 max-w-2xl mx-auto animate-pulse">
        <div className="h-6 bg-neutral-200 rounded w-48 mb-3"></div>
        <div className="h-4 bg-neutral-200 rounded w-full mb-4"></div>
        <div className="h-24 bg-neutral-100 rounded"></div>
      </div>
    );
  }

  // Don't show if there's an error - fail gracefully
  if (error) {
    return null;
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-6 max-w-2xl mx-auto shadow-sm">
      <h3 className="text-lg font-semibold text-neutral-900 mb-3">
        Serving California Counties
      </h3>
      <p className="text-neutral-600 mb-4">
        CalWMP currently provides CalGreen 5.408 compliant plans for:
      </p>

      {/* Featured county - San Luis Obispo */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3">
          <svg
            className="w-6 h-6 text-primary-600 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <div className="font-semibold text-primary-900">
              San Luis Obispo County
            </div>
            <div className="text-sm text-primary-700">
              Full coverage • All jurisdictions
            </div>
          </div>
          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
            Active
          </span>
        </div>
      </div>

      {/* Additional counties from API if available */}
      {counties.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-neutral-600 font-medium">
            Additional coverage:
          </p>
          <div className="grid grid-cols-2 gap-2">
            {counties.map((county) => (
              <div
                key={county.id}
                className="text-sm text-neutral-700 flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4 text-green-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{county.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact prompt for unsupported areas */}
      <div className="mt-4 pt-4 border-t border-neutral-200">
        <p className="text-sm text-neutral-600">
          Not in a supported area?{' '}
          <a
            href="/contact"
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            Request your county
          </a>
        </p>
      </div>
    </div>
  );
}
